import { ChartOptions, TooltipItem } from 'chart.js';
import { shortEnglishHumanizer } from './dateTime';
import { Context } from 'chartjs-plugin-datalabels';

export const compLabels = {
  case_number: 'Case Number',
  national_statistics: 'National Statistic',
  OR: 'Operating Room',
  modality: 'Modality',
  robot_type: 'Robot Type',
  surgeon_id: 'Surgeon',
  procedure: 'Procedure',
  specialty_code: 'Specialty',
  cases_per_day: 'Cases Per Day',
  weekday: 'Weekday',
  same_surgeon: 'Same Surgeon',
};

export function makeSimpleChartTooltipLabel(context: TooltipItem<'bar' | 'bubble'>) {
  const label = (context?.dataset as any)?.tooltipLabel as string;

  return `${label || ''} (${Math.ceil(context.parsed.y)})`;
}

export function displayScaleInMinutes(value: string | number) {
  let maybeNegative = '';
  if (Number(value) < 0) {
    maybeNegative = '-';
  }

  const minutes = Math.abs(Number(value));
  const milliseconds = minutes * 60 * 1000;

  const result = shortEnglishHumanizer(milliseconds, {
    round: true,
    units: ['h', 'm'],
    spacer: '',
  })
    .split(',')
    // .map((part) => {
    //   const numericPart = part.replace(/[^0-9]/g, '');
    //   const unit = part.replace(/[0-9]/g, '');
    //   return numericPart.padStart(2, '0') + unit;
    // })
    // .join(':')
    .map((i) => i.replace(/ /g, ' '))
    .join('');

  if (/m/.test(result) && /h/.test(result)) {
    // return maybeNegative + result.split(' ')[1];
  }

  return maybeNegative + result;
}

export const fontStyle = {
  size: 12,
  weight: 'bold',
  lineHeight: '20px',
  family: 'Lato',
};

export const XScaleGridStyle = {
  display: false,
  color: '#CECECE',
  borderDash: [5, 5],
};

export const YScaleGridStyle = {
  display: true,
  color: '#CECECE',
  borderDash: [5, 5],
};

export const scaleTicksStyle = {
  padding: 12,
  font: fontStyle,
  color: '#1C1C1F',
};
export const scaleTitleStyle = {
  display: true,
  font: fontStyle,
  color: '#1C1C1F',
};

export const commonOptions: Partial<ChartOptions> = {
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      intersect: true,
      displayColors: false,
      callbacks: {
        title: () => '',
        label: makeSimpleChartTooltipLabel,
      },
    },
    datalabels: {
      labels: {
        value: {
          font: {
            weight: 'bold',
          },
          color: '#000',
          borderRadius: 10,
          padding: {
            left: 8,
            right: 8,
            top: 5,
            bottom: 3,
          },
          clamp: true,
          clip: false,
          offset: 7,
          anchor: 'end',
          align: 'top',
          borderColor: '#F0F0F0',
          borderWidth: 1,
          backgroundColor: (context) => {
            return '#fff';
          },
          formatter: function (value, context: any) {
            return `${context.dataset?.datalabels?.[context.dataIndex] || 0}`;
          },
        },
      },
    },
  },
  scales: {
    x: {
      grid: XScaleGridStyle,
      ticks: scaleTicksStyle,
    },
    y: {
      grid: YScaleGridStyle,
      title: {
        ...scaleTitleStyle,
        text: 'Duration',
      },

      ticks: {
        ...scaleTicksStyle,
      },
    },
  },
};

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const displayDataLabels =
  (showDataLabels: boolean, isUtilizationChart = false, groupCount = 1000) =>
  (ctx: Context) => {
    if (ctx.dataset.type === 'scatter' || (ctx as any).dataset?.datalabels?.length === 0) {
      return false;
    }

    if (!isUtilizationChart && showDataLabels) {
      return true;
    }
    if (isUtilizationChart && showDataLabels) {
      // TODO: Old Method (Remove befoer pushing to UAT):
      // const activeItems: (string | undefined)[] = [];
      // ctx.chart.data.datasets.map((x) => (x.data.length > 0 ? activeItems.push(x.label) : null));
      // const showLabel = activeItems.slice(-groupCount).includes(ctx.dataset.label);

      // For Utilization, this is a bit complex. Since not all bars have all the phases, we need to check the
      // phases and see if the current phase is in the top modality for that phase
      // and if so, show the label
      const label = ctx.dataset.label?.toLowerCase().split(' ')[0];
      const day = (ctx.dataset as any)?.phaseNames?.[ctx.dataIndex];

      const modality = label?.split('-')[0].toLowerCase(); // AKA Phase
      const compare_item_cap = capitalize(label?.split('-')[1] || '');
      const compare_item_upper = label?.split('-')[1]?.toUpperCase();

      let topModality = 'other';
      for (const mod of ['other', 'open', 'lap', 'robotic']) {
        // <- Order matters here
        const datasetWithGeneral = ctx.dataset as any; // Cast to any to access general
        if (
          datasetWithGeneral?.general?.[`${mod}-${compare_item_cap}`]?.phaseNames
            ?.map((i: string) => i.toLowerCase())
            ?.includes(day?.toLowerCase()) ||
          datasetWithGeneral?.general?.[`${mod}-${compare_item_upper}`]?.phaseNames
            ?.map((i: string) => i.toLowerCase())
            ?.includes(day?.toLowerCase())
        ) {
          topModality = mod;
          break;
        }
      }
      return modality === topModality;
    }

    return false;
  };
